/**
 * Tipi TypeScript per il modulo Offerte
 */

// Stati possibili di un'offerta
export type StatoOfferta = 'bozza' | 'in_corso' | 'in_revisione' | 'approvata' | 'in_attesa' | 'confermata' | 'rifiutata'

// Configurazione tenant
export interface Configurazione {
  tariffa_oraria: number
  tariffa_km: number
  margine_desiderato: number
}

export interface ConfigurazioneUpdate {
  tariffa_oraria?: number
  tariffa_km?: number
  margine_desiderato?: number
}

// Cliente minimale (per dropdown e riferimenti)
export interface ClienteMinimal {
  id: string
  ragione_sociale: string
}

// Tipi costo
export type TipoCosto = 'diretto' | 'personale' | 'mezzo' | 'magazzino'

// Costi
export interface CostoDiretto {
  id: string
  tipo_costo: TipoCosto
  descrizione: string
  importo: number
  sottotipo: string | null
  quantita: number | null
  costo_unitario: number | null
  articolo_id: string | null
  integrativa_origin_id: string | null
}

export interface CostoDirettoCreate {
  tipo_costo: TipoCosto
  descrizione?: string
  importo?: number
  sottotipo?: string
  quantita?: number
}

export interface CostoMagazzinoCreate {
  articolo_id: string
  quantita: number
  costo_unitario?: number
  aggiungi_voce_libera: boolean
  voce_descrizione?: string
  voce_prezzo_unitario?: number
  voce_quantita?: number
}

// Voci (da listino o nota)
export type TipoVoce = 'voce' | 'nota'

export interface Voce {
  id: string
  listino_id: string | null
  codice_listino: string | null
  descrizione: string
  unita_misura: string
  quantita: number | null
  prezzo_unitario: number | null
  sconto_percentuale: number
  totale_riga: number | null
  ordine: number
  tipo: TipoVoce
  integrativa_origin_id: string | null
}

export interface VoceCreate {
  listino_id: string
  quantita: number
}

export interface VoceNotaCreate {
  descrizione: string
}

export interface VoceUpdate {
  quantita?: number
  sconto_percentuale?: number
  descrizione?: string
  prezzo_unitario?: number
  unita_misura?: string
}

export interface VoceLiberaCreate {
  descrizione: string
  unita_misura: string
  prezzo_unitario: number
  quantita: number
  sconto_percentuale: number
}

export interface VociRiordina {
  voce_ids: string[]
}

// Canoni ricorsivi
export type FrequenzaUnita = 'giorni' | 'settimane' | 'mesi' | 'anni'

export const FREQUENZA_LABELS: Record<FrequenzaUnita, string> = {
  giorni: 'Giorni',
  settimane: 'Settimane',
  mesi: 'Mesi',
  anni: 'Anni',
}

export interface CanoneRicorsivo {
  id: string
  listino_id: string | null
  codice_listino: string | null
  descrizione: string
  unita_misura: string
  quantita: number | null
  prezzo_unitario: number | null
  sconto_percentuale: number
  totale_riga: number | null
  ordine: number
  tipo: TipoVoce
  frequenza_valore: number
  frequenza_unita: FrequenzaUnita
  giorni_preavviso: number
}

export interface CanoneCreate {
  listino_id: string
  quantita: number
  frequenza_valore: number
  frequenza_unita: FrequenzaUnita
  giorni_preavviso?: number
}

export interface CanoneLiberoCrea {
  descrizione: string
  unita_misura: string
  prezzo_unitario: number
  quantita: number
  sconto_percentuale: number
  frequenza_valore: number
  frequenza_unita: FrequenzaUnita
  giorni_preavviso?: number
}

export interface CanoneNotaCreate {
  descrizione: string
}

export interface CanoneUpdate {
  quantita?: number
  sconto_percentuale?: number
  descrizione?: string
  prezzo_unitario?: number
  unita_misura?: string
  frequenza_valore?: number
  frequenza_unita?: FrequenzaUnita
  giorni_preavviso?: number
}

export interface CanoniRiordina {
  canone_ids: string[]
}

// Scaglioni di pagamento
export type TipoScaglione = 'percentuale' | 'importo'

export interface Scaglione {
  id: string
  descrizione: string
  tipo: TipoScaglione
  percentuale: number | null
  importo: number | null
  importo_calcolato: number
  ordine: number
}

export interface ScaglioneCreate {
  descrizione: string
  tipo: TipoScaglione
  percentuale?: number
  importo?: number
}

// Costi calcolati
export interface CostiCalcolati {
  costi_diretti_totale: number
  costo_personale_totale: number
  costo_mezzi_totale: number
  costo_magazzino_totale: number
  totale_costi: number
}

// Tariffe configurazione
export interface Tariffa {
  id: string
  tipo: string
  chiave: string
  tariffa: number
  unita: string
}

export interface TariffeConfigurazione {
  personale: Tariffa[]
  mezzi: Tariffa[]
  margine_desiderato: number
}

// Tipo offerta
export type TipoOfferta = 'standard' | 'integrativa'

// Offerta - versione lista
export interface OffertaList {
  id: string
  numero_offerta: string
  titolo: string
  cliente: ClienteMinimal
  stato: StatoOfferta
  owner_id: string | null
  owner_name: string | null
  citta_consegna: string | null
  totale_valore: number
  totale_canoni_ricorsivi: number
  margine_percentuale: number
  stock_status: string | null
  created_at: string
  data_scadenza: string | null
  tipo_offerta: TipoOfferta
}

// Integrativa (item lista dentro offerta padre)
export interface IntegrativaListItem {
  id: string
  numero_offerta: string
  numero_integrativa: number
  titolo: string
  stato: StatoOfferta
  totale_valore: number
  totale_costi: number
  giorni_personale: number
  created_at: string
}

// Referente minimale (per offerta)
export interface ReferenteMinimal {
  id: string
  nome: string
  cognome: string
  email: string | null
  telefono: string | null
  ruolo: string | null
}

// Offerta - versione dettaglio completo
export interface OffertaDetail {
  id: string
  numero_offerta: string
  titolo: string
  descrizione: string | null
  descrizione_stampa: string | null
  cliente: ClienteMinimal
  referente: ReferenteMinimal | null
  stato: StatoOfferta
  owner_id: string | null
  owner_name: string | null
  indirizzo_consegna: string | null
  citta_consegna: string | null
  cap_consegna: string | null
  provincia_consegna: string | null
  note: string | null
  validita_giorni: number
  data_scadenza: string | null
  costi_diretti: CostoDiretto[]
  voci: Voce[]
  scaglioni: Scaglione[]
  canoni_ricorsivi: CanoneRicorsivo[]
  costi_calcolati: CostiCalcolati
  totale_costi: number
  subtotale_voci: number
  sconto_finale_percentuale: number
  totale_valore: number
  totale_scaglioni: number
  totale_canoni_ricorsivi: number
  margine_percentuale: number
  margine_desiderato: number
  alert_margine: boolean
  has_documento: boolean
  stock_status: string | null
  documento_filename: string | null
  data_generazione_documento: string | null
  numero_revisione: number
  created_at: string
  updated_at: string | null
  // Integrativa fields
  tipo_offerta: TipoOfferta
  parent_offerta_id: string | null
  numero_integrativa: number | null
  integrative: IntegrativaListItem[]
  totale_valore_consolidato: number
  totale_costi_consolidato: number
  giorni_personale_consolidato: number
  integrativa_labels: Record<string, string>
}

// Revisione offerta
export interface OffertaRevisione {
  id: string
  numero_revisione: number
  data_revisione: string | null
  note_revisione: string | null
  creato_da: string | null
  has_documento: boolean
  documento_filename: string | null
  snapshot_totale_valore: number | null
  snapshot_totale_costi: number | null
  created_at: string
}

// Discrepanza documento
export interface Discrepanza {
  campo: string
  valore_db: string
  valore_documento: string
  tipo: string
  severita: 'critico' | 'warning'
  descrizione: string
}

export interface VerificaDocumentoResult {
  offerta_id: string
  discrepanze: Discrepanza[]
  esito: 'ok' | 'discrepanze_trovate'
  totale_discrepanze: number
  critiche: number
  warning: number
}

// Create/Update
export interface OffertaCreate {
  titolo: string
  descrizione?: string
  descrizione_stampa?: string
  cliente_id: string
  referente_id?: string
  owner_id?: string
  indirizzo_consegna?: string
  citta_consegna?: string
  cap_consegna?: string
  provincia_consegna?: string
  note?: string
  validita_giorni?: number
}

export interface OffertaUpdate {
  titolo?: string
  descrizione?: string
  descrizione_stampa?: string
  cliente_id?: string
  referente_id?: string | null
  stato?: StatoOfferta
  owner_id?: string
  indirizzo_consegna?: string
  citta_consegna?: string
  cap_consegna?: string
  provincia_consegna?: string
  note?: string
  validita_giorni?: number
  sconto_finale_percentuale?: number
  gestore_pagamento_id?: string
}

// Listino per selezione voci
export interface ListinoItem {
  id: string
  codice: string
  descrizione: string
  prezzo_unitario: number
  unita_misura: string
  attivo: boolean
}

// Sede cliente
export interface SedeCliente {
  id: string
  cliente_id: string
  descrizione: string
  via: string | null
  citta: string | null
  cap: string | null
  provincia: string | null
  note: string | null
  created_at: string
}

export interface SedeCreate {
  descrizione: string
  via?: string
  citta?: string
  cap?: string
  provincia?: string
  note?: string
}

export interface SedeUpdate {
  descrizione?: string
  via?: string
  citta?: string
  cap?: string
  provincia?: string
  note?: string
}

// Referente cliente
export interface ReferenteCliente {
  id: string
  cliente_id: string
  nome: string
  cognome: string
  email: string | null
  telefono: string | null
  ruolo: string | null
  note: string | null
  is_preferito?: boolean
  created_at: string
}

export interface ReferenteCreate {
  nome: string
  cognome: string
  email?: string
  telefono?: string
  ruolo?: string
  note?: string
}

export interface ReferenteUpdate {
  nome?: string
  cognome?: string
  email?: string
  telefono?: string
  ruolo?: string
  note?: string
}

// Indirizzo option (per dropdown offerta)
export interface IndirizzoOption {
  label: string
  via: string | null
  citta: string | null
  cap: string | null
  provincia: string | null
  sede_id: string | null
  is_preferita?: boolean
}

// Cliente per selezione
export interface Cliente {
  id: string
  ragione_sociale: string
  p_iva: string | null
  via: string | null
  citta: string | null
  cap: string | null
  provincia: string | null
  email: string | null
  telefono: string | null
  attivo: boolean
  note: string | null
  sedi: SedeCliente[]
  referenti: ReferenteCliente[]
  created_at: string
}
